import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import api from "@/services/api";
import DashboardLayout from "@/components/layout/DashboardLayout";
import CertificateDownload from "@/components/common/CertificateDownload";
import InlineCertificateViewer from "@/components/common/InlineCertificateViewer";
import { LetterDownload, InlineLetterViewer } from "@/components/common/LetterOfGoodStanding";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, FileText, ChevronLeft, ChevronRight, PanelRightOpen, Trash2 } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { memberNumberOf } from "@/lib/membershipNumber";

const STATUS_PILL: Record<string, { label: string; cls: string }> = {
  pending: { label: "Pending review", cls: "bg-amber-100 text-amber-700" },
  document_verified: { label: "Docs verified", cls: "bg-violet-100 text-violet-700" },
  course_assigned: { label: "Course assigned", cls: "bg-violet-100 text-violet-700" },
  eligible_for_exam: { label: "Eligible for exam", cls: "bg-violet-100 text-violet-700" },
  exception: { label: "Exemption", cls: "bg-violet-100 text-violet-700" },
  sent_for_approval: { label: "Sent for approval", cls: "bg-blue-100 text-blue-700" },
  approved: { label: "Approved", cls: "bg-green-100 text-green-700" },
  paid: { label: "Certified", cls: "bg-green-100 text-green-700" },
  certified: { label: "Certified", cls: "bg-green-100 text-green-700" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
};
const AVATAR_COLORS = ["#6d28d9", "#4f46e5", "#0e7490", "#b45309", "#15803d", "#be185d"];
const avatarColor = (s: string) => AVATAR_COLORS[[...(s || "?")].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
const initials = (name: string) => (name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
const statusPill = (app: any) => {
  if (!app) return { label: "No application", cls: "bg-muted text-muted-foreground" };
  if (app.payment_status === "paid" || app.status === "certified") return { label: "Certified", cls: "bg-green-100 text-green-700" };
  return STATUS_PILL[app.status] || { label: app.status, cls: "bg-muted text-muted-foreground" };
};

interface StudentRow {
  user_id: string;
  full_name: string;
  email: string;
  phone: string | null;
  created_at: string;
  application: any | null;
}

const PAGE_SIZE = 10;

const Students = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchStudents = async () => {
    const { data: studentRoles } = await supabase
      .from("user_roles")
      .select("user_id")
      .eq("role", "student");

    if (!studentRoles || studentRoles.length === 0) {
      setStudents([]);
      setLoading(false);
      return;
    }

    const studentIds = studentRoles.map((r) => r.user_id);

    const [{ data: profiles }, { data: applications }] = await Promise.all([
      supabase.from("profiles").select("user_id, full_name, email, phone, created_at").in("user_id", studentIds),
      supabase.from("applications").select("*").in("user_id", studentIds),
    ]);

    const appMap = new Map<string, any>();
    applications?.forEach((a) => {
      if (!appMap.has(a.user_id) || new Date(a.created_at) > new Date(appMap.get(a.user_id).created_at)) {
        appMap.set(a.user_id, a);
      }
    });

    const merged: StudentRow[] = (profiles || []).map((p) => ({
      user_id: p.user_id,
      full_name: p.full_name,
      email: p.email,
      phone: p.phone,
      created_at: p.created_at,
      application: appMap.get(p.user_id) || null,
    }));

    setStudents(merged);
    setLoading(false);
  };

  const handleDelete = async (userId: string) => {
    setDeletingId(userId);
    try {
      await api.delete(`/users/${userId}`);
      toast({ title: "Student deleted" });
      fetchStudents();
    } catch (e: any) {
      toast({ title: "Delete failed", description: e.response?.data?.error || e.message, variant: "destructive" });
    }
    setDeletingId(null);
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const filtered = students.filter(
    (s) =>
      s.full_name.toLowerCase().includes(search.toLowerCase()) ||
      s.email.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search]);

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <PageHeader
          title="Students"
          badge={
            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> Live
            </span>
          }
          actions={
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search students..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-11 rounded-xl pl-10 pr-9"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              )}
            </div>
          }
        />

        {/* Table */}
        <Card className="glass-premium overflow-hidden rounded-2xl">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : (
              <div className="overflow-x-auto">
              <Table>
                 <TableHeader>
                  <TableRow className="hover:bg-transparent border-border">
                    <TableHead className="w-12 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">S/No</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Name / Email</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Registered</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Certificate</TableHead>
                    <TableHead className="text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginated.map((student, idx) => (
                    <TableRow key={student.user_id} className="border-border hover:bg-muted/40 transition-colors">
                      <TableCell className="text-xs text-muted-foreground tabular-nums">{(page - 1) * PAGE_SIZE + idx + 1}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-xs font-bold text-white" style={{ background: avatarColor(student.full_name) }}>
                            {initials(student.full_name)}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground">{student.full_name}</p>
                            <p className="text-xs text-muted-foreground">{student.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(student.created_at), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const p = statusPill(student.application);
                          return <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${p.cls}`}>{p.label}</span>;
                        })()}
                      </TableCell>
                      <TableCell>
                        {student.application && (student.application.payment_status === "paid" || student.application.status === "certified") ? (
                          <div className="flex gap-1">
                            <InlineCertificateViewer
                              fullName={student.full_name}
                              certId={memberNumberOf(student.application)}
                              date={format(new Date(student.application.updated_at), "MMMM d, yyyy")}
                            />
                            <CertificateDownload
                              fullName={student.full_name}
                              certId={memberNumberOf(student.application)}
                              date={format(new Date(student.application.updated_at), "MMMM d, yyyy")}
                              applicationId={student.application.id}
                            />
                            <InlineLetterViewer
                              fullName={student.full_name}
                              memberNum={memberNumberOf(student.application)}
                              date={format(new Date(student.application.updated_at), "MMMM d, yyyy")}
                              city={student.application.city}
                              country={student.application.country}
                              gender={student.application.gender}
                            />
                            <LetterDownload
                              fullName={student.full_name}
                              memberNum={memberNumberOf(student.application)}
                              date={format(new Date(student.application.updated_at), "MMMM d, yyyy")}
                              city={student.application.city}
                              country={student.application.country}
                              gender={student.application.gender}
                            />
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            title="View Student"
                            className="h-8 w-8 p-0 border-border/60 hover:bg-primary hover:text-white hover:border-primary transition-all duration-200"
                            onClick={() => navigate(`/growlearn/students/${student.user_id}`)}
                          >
                            <PanelRightOpen className="h-3.5 w-3.5" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                title="Delete Student"
                                className="h-8 w-8 p-0 border-border/60 hover:bg-red-500 hover:text-white hover:border-red-500 transition-all duration-200"
                                disabled={deletingId === student.user_id}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete student?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete <strong>{student.full_name}</strong> ({student.email}) and all their data. This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-red-500 hover:bg-red-600 text-white"
                                  onClick={() => handleDelete(student.user_id)}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                        <FileText className="mx-auto h-8 w-8 text-muted-foreground/30 mb-2" />
                        No students found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
            )}
            {!loading && filtered.length > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
                <p className="text-xs text-muted-foreground">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} students
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="h-8 w-8 flex items-center justify-center rounded-md text-xs font-semibold gradient-warm text-white">
                    {page}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

    </DashboardLayout>
  );
};

export default Students;
